import { useEffect, useState, type FormEvent } from 'react';
import { SETTING_KEYS } from '@hrlabs/shared-types';
import { getRow, saveRow } from '../../lib/db';
import { useToast } from '../../components/Toast';

export function AiTranslation() {
  const { notify } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');

  useEffect(() => {
    Promise.all([
      getRow('settings', SETTING_KEYS.aiSystemPrompt),
      getRow('settings', SETTING_KEYS.aiUserPrompt),
    ]).then(([sys, usr]) => {
      setSystemPrompt(String(sys?.value ?? ''));
      setUserPrompt(String(usr?.value ?? ''));
      setLoading(false);
    });
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (systemPrompt.trim().length < 50) {
      notify('danger', 'System prompt must be at least 50 characters.');
      return;
    }
    setSaving(true);
    try {
      await saveRow('settings', SETTING_KEYS.aiSystemPrompt, {
        value: systemPrompt,
        type: 'text',
        group: 'ai',
      });
      await saveRow('settings', SETTING_KEYS.aiUserPrompt, {
        value: userPrompt,
        type: 'text',
        group: 'ai',
      });
      notify('success', 'AI translation settings saved.');
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 820 }}>
      <h3 className="fw-bold mb-3">AI Translation</h3>
      <div className="mb-3">
        <label className="form-label fw-semibold">System prompt</label>
        <textarea
          className="form-control"
          rows={10}
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
        />
      </div>
      <div className="mb-3">
        <label className="form-label fw-semibold">User prompt</label>
        <textarea
          className="form-control"
          rows={4}
          value={userPrompt}
          onChange={(e) => setUserPrompt(e.target.value)}
        />
      </div>
      <p className="small text-muted">
        Translation runs against OpenAI when <code>OPENAI_API_KEY</code> is set on the Functions
        runtime; otherwise it returns a <code>[DEV EN]</code> stub.
      </p>
      <button className="btn btn-success" disabled={saving} type="submit">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}
